import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { MetaTagDescriptor } from "@components/MetaTags/MetaTags";
import { Route } from "@route-types/Keyword/+types/KeywordPage";
import { fetchBudgetPageData } from "@routes/SingaporeBudget/server/SingaporeBudgetPage.server";
import SingaporeBudgetPageLayoutFactory from "@routes/SingaporeBudget/SingaporeBudgetPageLayoutFactory";
import { getBudgetData } from "@routes/SingaporeBudget/utils/helpers";
import { BudgetContext } from "@routes/SingaporeBudget/utils/types";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import { HttpStatusCode } from "axios";

/** This is the main entry point component for /keyword/singapore-budget-2024 and /keyword/singapore-budget-2025 route. */
const SingaporeBudgetPage = (): React.ReactElement => {
  const context = useLoaderData<BudgetContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  const data = getBudgetData(context.payload.data);

  if (!data) {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  return (
    <SingaporeBudgetPageLayoutFactory
      data={data}
      gaProps={context.payload.gaProps}
    />
  );
};

export default SingaporeBudgetPage;

export const loader = async ({
  request,
}: Route.LoaderArgs): Promise<BudgetContext> => {
  const url = new URL(request.url, `http://localhost`);
  const path = url.pathname;
  const envDomain = getEnvironmentDomain(getEnvironment());
  const absolutePath = `${envDomain}${path}`;

  return await fetchBudgetPageData(absolutePath);
};

export const meta = ({ loaderData }: Route.MetaArgs): MetaTagDescriptor[] => {
  const context = loaderData as BudgetContext;

  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return data.metaTags;
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as {
    context?: BudgetContext;
  };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
